import { ILiveWorkout, IUserWorkoutDetail } from '@ifit/gateway-wolf-dashboard-service';
import LiveWorkoutItem from './live-workout-item';
import { useLiveWorkouts } from '../../state-management/server-state/use-live-workouts';
import RowHeading from '../row-heading';
import CardSkeleton from '@ifit/mp-card-skeleton';
import { ONE_MINUTE } from '../../util/durations';
import { useRerenderOnTimer } from '../../util/use-rerender-on-timer';
import { isRestrictedUserType } from '../../util/is-restricted-user-type';
import { useUserWorkoutDetails } from '../../state-management/server-state/use-user-workout-details';
import { isVaultVue } from '../../util/is-vault-vue';
import { isClub } from '../../util/is-club';
import { isMatrix } from '../../util/is-matrix';
import { useSoftwareNumber } from '../../state-management/selectors/use-software-number';
import { useSupportsDeepLinksV1 } from '../../state-management/selectors/use-supports-deep-links-v1';
import { useUserType } from '../../state-management/selectors/use-user-type';
import { Text } from './text';
import { useMatrixCommercial } from '../../hooks/use-matrix-commercial';
import { FeatureFlag, FeatureFlags } from '../../types';
import { useFeatureFlags } from '../../hooks/use-feature-flags';
import './index.scss';

const skeletonCards = Array(10).fill(0);

export default function LiveWorkoutsRow(): JSX.Element {
  const softwareNumber = useSoftwareNumber();
  const supportsDeepLinksV1 = useSupportsDeepLinksV1();
  const userType = useUserType();
  const isMatrixCommerical = useMatrixCommercial();
  const featureFlags = useFeatureFlags([FeatureFlag.LiveWorkouts]);
  const isRestrictedUser = isRestrictedUserType(userType);
  const matrixClubRestrictedUser = isMatrix(softwareNumber) && isClub(softwareNumber) && isRestrictedUser;
  const shouldDisableCall = matrixClubRestrictedUser || isVaultVue(softwareNumber) || !supportsDeepLinksV1;

  useRerenderOnTimer(ONE_MINUTE);
  const liveWorkoutsQuery = useLiveWorkouts(shouldDisableCall);

  const workoutIds = liveWorkoutsQuery.data?.map((liveWorkout) => liveWorkout.workout.id).filter(Boolean);
  const userWorkoutDetailsQuery = useUserWorkoutDetails(workoutIds);

  if (liveWorkoutsQuery.data) {
    const futureLiveWorkouts = liveWorkoutsQuery.data?.filter(
      (liveWorkout) => liveWorkout.schedule.joinableUntil > Date.now()
    );
    liveWorkoutsQuery.data = futureLiveWorkouts;
  }

  const hideRow = shouldHideRow({
    error: liveWorkoutsQuery.error,
    isSuccess: liveWorkoutsQuery.isSuccess,
    data: liveWorkoutsQuery.data,
    shouldDisableCall,
    isMatrixCommerical,
    featureFlags,
  });

  if (hideRow) {
    return null;
  }

  const zippedData = liveWorkoutsQuery.data?.map((liveWorkout) => {
    return {
      liveWorkout,
      userWorkoutDetail: userWorkoutDetailsQuery.data?.find((detail) => detail.workoutId === liveWorkout.workout?.id),
    };
  });

  let content: JSX.Element[];
  if (liveWorkoutsQuery.isLoading) {
    content = skeletonCards.map((_, index) => <CardSkeleton key={index} className="custom" />);
  } else {
    content = zippedData.map((item) => <LiveWorkoutItem key={item.liveWorkout.workout.id} item={item} />);
  }
  return (
    <div className="row-live">
      <RowHeading
        title={Text.title}
        fetching={liveWorkoutsQuery.isFetching || userWorkoutDetailsQuery.isFetching}
        link="https://www.ifit.com/live-schedule"
      />
      <div className="cards" style={{ ['--cards-per-row' as any]: content.length }}>
        {content}
      </div>
    </div>
  );
}

export interface ILiveWorkoutAndDetails {
  liveWorkout: ILiveWorkout;
  userWorkoutDetail: IUserWorkoutDetail;
}

export function liveWorkoutsRowItem(item: ILiveWorkoutAndDetails): JSX.Element {
  return <LiveWorkoutItem item={item} />;
}

interface IHideRowContext {
  data: ILiveWorkout[];
  isSuccess: boolean;
  error: Error;
  shouldDisableCall: boolean;
  isMatrixCommerical: boolean;
  featureFlags: Partial<FeatureFlags>;
}

function shouldHideRow(rowContext: IHideRowContext): boolean {
  const { error, isSuccess, data, shouldDisableCall, isMatrixCommerical, featureFlags } = rowContext;

  const noValidData = !data || data.length === 0;
  const successWithNoData = noValidData && isSuccess;
  const errorWithNoCachedData = error && noValidData;
  const isFeatureFlagEnabled = featureFlags[FeatureFlag.LiveWorkouts] === true;

  return Boolean(
    shouldDisableCall || errorWithNoCachedData || successWithNoData || isMatrixCommerical || !isFeatureFlagEnabled
  );
}
